import {
  CLR_CHAIN_SLOTS,
  DOA_FALLBACK_RISK_CLASS,
  DOA_RISK_CLASS_BY_RISK_CLASS_CODE,
  matchAllRiskBandByAmount,
  toMappedDoaRiskClass,
  type ClrChainSlot,
  type ClrDecision,
} from './credit-limit-reviews'
import {
  BLOCKED_ORDER_OPTION_KEYS,
  DOA_BASE_SLOTS,
  DOA_OPTIONS_BY_TYPE,
  toDoaBaseSlot,
  type BlockedOrderDoaOption,
  type DoaBaseSlot,
  type DoaOptionKey,
  type DoaRiskClass,
  type DoaRoleSlot,
} from './delegation-of-authority'

/**
 * Blocked-orders delegation of authority.
 *
 * Separate from `delegation-of-authority.ts` because the chain builder needs `CLR_CHAIN_SLOTS`,
 * which lives in `credit-limit-reviews.ts` and cannot be imported there without a cycle. Nothing
 * imports back out of this file.
 *
 * The shape differs from CLR in one way the shared `DoaOption` cannot express: the approver is a
 * per-band flag (`requiresApprover`) rather than always present. Hence `isBlockedOrderSlotEnabled`
 * and `getBlockedOrderChainSlots` rather than the CLR versions. (There is no preparer either — the
 * system creates the record from the incoming IDOC — but `hasPreparer` says so on the option.)
 */

/**
 * PCE% is exposure ÷ credit limit, so 200 means "twice their limit". Under the ceiling a clean
 * account stays with the GBS analyst; over it, the record escalates.
 *
 * Constants rather than configurable rows: they are thresholds on a derived percentage, not
 * authority bands, and BAT treats them as policy. Changing one is a code change, the same as
 * changing a dollar band.
 */
/**
 * The SAP risk class codes the blocked-orders ladder is defined for: the four that map to a DOA
 * risk class. Any other code on the payer, or none, is a master-data problem the cockpit flags
 * (BAT daily, 2026-10-28); the resolver still routes such an order as High Risk and escalated.
 */
export const BLOCKED_ORDER_VALID_RISK_CLASS_CODES = (
  Object.keys(
    DOA_RISK_CLASS_BY_RISK_CLASS_CODE,
  ) as (keyof typeof DOA_RISK_CLASS_BY_RISK_CLASS_CODE)[]
).filter((code) => DOA_RISK_CLASS_BY_RISK_CLASS_CODE[code] !== null)

export function isBlockedOrderRiskClassValid(code: string | null | undefined): boolean {
  return toMappedDoaRiskClass(code ?? null) !== null
}

export const PCE_CEILING_BY_RISK_CLASS = {
  very_low: 200,
  low: 200,
  medium: 200,
  high: 150,
} as const satisfies Partial<Record<DoaRiskClass, number>>

const BLOCKED_ORDER_OPTIONS = DOA_OPTIONS_BY_TYPE.blocked_orders

export function getBlockedOrderDoaOption(key: DoaOptionKey): BlockedOrderDoaOption | undefined {
  return (BLOCKED_ORDER_OPTIONS as Partial<Record<DoaOptionKey, BlockedOrderDoaOption>>)[key]
}

export function getBlockedOrderDoaOptions(): {
  key: DoaOptionKey
  option: BlockedOrderDoaOption
}[] {
  return BLOCKED_ORDER_OPTION_KEYS.flatMap((key) => {
    const option = getBlockedOrderDoaOption(key)
    return option ? [{ key, option }] : []
  })
}

/**
 * The blocked-orders counterpart of `isDoaSlotEnabled`. The preparer is off for every band and
 * Reviewer 2 is never used, so only the reviewer and approver flags are read. Delegates mirror
 * their base slot, exactly as in CLR.
 *
 * Note the slot stays `reviewer_1` here even though the UI labels it plainly "Reviewer" for this
 * type. The identifier is persisted — `delegation_of_authority_assignments.doa_role_type`, and the
 * CLR chain's `pending_slot` / `acted_as_slot` — and shared with every other DOA type, so it is not
 * a display name. Blocked orders drops the number on screen only because it can never enable a
 * Reviewer 2; see `slotLabelKeyFor` in `DelegationOfAuthorityPage.tsx`.
 */
export function isBlockedOrderSlotEnabled(
  option: BlockedOrderDoaOption,
  slot: DoaRoleSlot,
): boolean {
  switch (toDoaBaseSlot(slot)) {
    case 'preparer':
      return option.hasPreparer
    case 'reviewer_1':
      return option.requiresReviewer1
    case 'reviewer_2':
      return false
    case 'approver':
      return option.requiresApprover
  }
}

/** The matrix columns for the blocked-orders page: the base slots at least one band enables. */
export function getBlockedOrderBaseSlots(): DoaBaseSlot[] {
  return DOA_BASE_SLOTS.filter((slot) =>
    getBlockedOrderDoaOptions().some(({ option }) => isBlockedOrderSlotEnabled(option, slot)),
  )
}

/**
 * The ordered actionable chain for a band. Unlike `getClrChainSlots` the approver is not
 * guaranteed by the shape: a band with `requiresApprover: false` would end at the reviewer. Returns `[]` for a key that is
 * not a blocked-orders band, which callers must treat as "no chain, cannot proceed".
 */
export function getBlockedOrderChainSlots(optionKey: DoaOptionKey): ClrChainSlot[] {
  const option = getBlockedOrderDoaOption(optionKey)
  if (!option) return []
  return CLR_CHAIN_SLOTS.filter((slot) => {
    if (slot === 'reviewer_1') return option.requiresReviewer1
    if (slot === 'reviewer_2') return option.requiresReviewer2
    return option.requiresApprover
  })
}

export function getFirstBlockedOrderChainSlot(optionKey: DoaOptionKey): ClrChainSlot | null {
  return getBlockedOrderChainSlots(optionKey)[0] ?? null
}

type BlockedOrderResolverInput = {
  /**
   * The payer's credit limit exceeded amount, as received from SAP. Payer-level and cumulative: it
   * already includes the payer's blocked, unreleased orders, and it is never something Fuze sums
   * or maxes across orders. Resolve once, when the batch is sent for approval.
   */
  ceb: number
  /** Exposure ÷ credit limit, as a percentage. Only read at or below $35K. */
  pcePercent: number | null
  currentRiskClassCode: string | null
  /** Return item — a failed payment / NSF. */
  hasReturnItem: boolean
  isAccountUnder120Days: boolean
  isNewExposureOverHighestPayment: boolean
}

const CEB_ANALYST_CEILING = 35_000
const CEB_RISK_SPLIT_CEILING = 100_000
const CEB_MEDIUM_HIGH_SPLIT = 50_000

/**
 * Resolves the blocked-orders DOA band from the CEB. Total: every order gets a band.
 *
 * A risk class the DOA has no row for — Z01 provisional, Z07 block, none at all, or a code we
 * cannot read — routes as `DOA_FALLBACK_RISK_CLASS` (high) AND escalated, at every tier: the
 * escalated all-risk band at or below $35K, the escalated high band at $35K–$50K, the high band at
 * $50K–$100K (which has no escalated twin), and the all-risk bands above. Those accounts should
 * not be ordering at all, so the decision goes to the strictest authority the band has rather than
 * to nobody: stranding an order against its delivery cutoff is worse than routing it too high.
 */
export function resolveBlockedOrderDoaOptionKey(input: BlockedOrderResolverInput): DoaOptionKey {
  const mappedRiskClass = toMappedDoaRiskClass(input.currentRiskClassCode)
  const riskClass = mappedRiskClass ?? DOA_FALLBACK_RISK_CLASS

  // A CEB at or below zero falls through to the bottom band like any other amount up to $35K,
  // escalation and PCE% included.
  // Any one of the triggers routes the same way, so they collapse to a single flag.
  const isEscalated =
    mappedRiskClass === null ||
    input.hasReturnItem ||
    input.isAccountUnder120Days ||
    input.isNewExposureOverHighestPayment

  if (input.ceb <= CEB_ANALYST_CEILING) {
    return isEscalated || isOverPceCeiling(riskClass, input.pcePercent)
      ? 'blocked_orders__all__lte_35k__escalated'
      : 'blocked_orders__all__lte_35k'
  }

  if (input.ceb <= CEB_RISK_SPLIT_CEILING) {
    return resolveRiskSplitBand(riskClass, input.ceb, isEscalated)
  }

  // Above $100K the bands are all-risk and there is no escalation axis.
  return (
    matchAllRiskBandByAmount(input.ceb, BLOCKED_ORDER_OPTION_KEYS) ?? 'blocked_orders__all__gt_5m'
  )
}

/** The four risk classes the blocked-orders ladder is defined for. */
type LadderRiskClass = keyof typeof PCE_CEILING_BY_RISK_CLASS

/**
 * True when the account's PCE% is over the ceiling for its risk class. A missing PCE% escalates
 * too, since we cannot show it is under the bar.
 */
function isOverPceCeiling(riskClass: LadderRiskClass, pcePercent: number | null): boolean {
  if (pcePercent === null) return true
  return pcePercent > PCE_CEILING_BY_RISK_CLASS[riskClass]
}

/**
 * The $35K–$100K tier, where BAT splits the risk classes asymmetrically: very low and low share one
 * wide band, medium and high break again at $50K.
 */
function resolveRiskSplitBand(
  riskClass: LadderRiskClass,
  ceb: number,
  isEscalated: boolean,
): DoaOptionKey {
  switch (riskClass) {
    case 'very_low':
      return isEscalated
        ? 'blocked_orders__very_low__35k_100k__escalated'
        : 'blocked_orders__very_low__35k_100k'
    case 'low':
      return isEscalated
        ? 'blocked_orders__low__35k_100k__escalated'
        : 'blocked_orders__low__35k_100k'
    case 'medium':
      if (ceb > CEB_MEDIUM_HIGH_SPLIT) return 'blocked_orders__medium__50k_100k'
      return isEscalated
        ? 'blocked_orders__medium__35k_50k__escalated'
        : 'blocked_orders__medium__35k_50k'
    case 'high':
      if (ceb > CEB_MEDIUM_HIGH_SPLIT) return 'blocked_orders__high__50k_100k'
      return isEscalated
        ? 'blocked_orders__high__35k_50k__escalated'
        : 'blocked_orders__high__35k_50k'
  }
}

/**
 * Standard and escalated twins share their bounds and risk class, so the config page needs this to
 * tell two otherwise identical rows apart.
 */
export const BLOCKED_ORDER_ESCALATED_SUFFIX = '__escalated'

export function isEscalatedBlockedOrderOptionKey(key: DoaOptionKey): boolean {
  return key.endsWith(BLOCKED_ORDER_ESCALATED_SUFFIX)
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Review workflow (CT-31)
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Where one blocked order's record sits (CT-8). One record per order: decisions, chain and history
 * are all the order's own, and the payer-level CEB is only the input the band is resolved from.
 *
 * Two terminal states, as CLR has: the status IS the outcome, so there is no separate per-order
 * decision to keep in step with it.
 *
 * No send-back state. A blocked order's chain only moves forward — the reviewer releases (or sends
 * for approval) or rejects, and so does the approver. A cutoff is ticking, and a round trip back to
 * the reviewer is a day lost for the shipment.
 */
export const BLOCKED_ORDER_REVIEW_STATUSES = [
  'pending_review',
  'pending_approval',
  'released',
  'rejected',
] as const
export type BlockedOrderReviewStatus = (typeof BLOCKED_ORDER_REVIEW_STATUSES)[number]

/** How a decided order ended: the two terminal statuses. */
export type BlockedOrderOutcome = Extract<BlockedOrderReviewStatus, 'released' | 'rejected'>

/**
 * What the cockpit shows for one order — BAT's "Status" column (Awaiting Review/Approval, Released,
 * Rejected). The same set as the record's status now that a record is one order.
 *
 * No `new`: a blocked order is either assigned to a DOA chain or does not exist in the cockpit. The
 * orders routing skips (a risk class with no ladder) are left out of the list rather than shown
 * with a status that implies someone will pick them up.
 */
export const BLOCKED_ORDER_COCKPIT_STATUSES = BLOCKED_ORDER_REVIEW_STATUSES
export type BlockedOrderCockpitStatus = BlockedOrderReviewStatus

/**
 * SAP's reason for the credit case (`REASON` on the IDOC), from BAT's "Field Values" sheet. The
 * labels live in i18n; a code outside this list is shown raw.
 */
export const BLOCKED_ORDER_REASON_CODES = [
  '0000',
  '0001',
  '0002',
  '0003',
  '0004',
  '0005',
  '0006',
  '0007',
  '0008',
  '0009',
  '0010',
] as const
export type BlockedOrderReasonCode = (typeof BLOCKED_ORDER_REASON_CODES)[number]

export function isBlockedOrderReasonCode(
  value: string | null | undefined,
): value is BlockedOrderReasonCode {
  return value != null && (BLOCKED_ORDER_REASON_CODES as readonly string[]).includes(value)
}

/** Statuses in which a record is still in its chain. */
export const BLOCKED_ORDER_OPEN_STATUSES = [
  'pending_review',
  'pending_approval',
] as const satisfies readonly BlockedOrderReviewStatus[]

export function isBlockedOrderReviewOpen(status: BlockedOrderReviewStatus): boolean {
  return (BLOCKED_ORDER_OPEN_STATUSES as readonly BlockedOrderReviewStatus[]).includes(status)
}

/** The released or rejected states, where the record's detail page is frozen. */
export function isBlockedOrderReviewResolved(status: BlockedOrderReviewStatus): boolean {
  return status === 'released' || status === 'rejected'
}

/**
 * What a chain actor may do with a blocked order: `advance` (send for approval, or release at the
 * last slot) or `reject`. The CLR verbs minus `send_back` — see the status comment above.
 */
export const BLOCKED_ORDER_DECISIONS = [
  'advance',
  'reject',
] as const satisfies readonly ClrDecision[]
export type BlockedOrderDecision = (typeof BLOCKED_ORDER_DECISIONS)[number]

export function isBlockedOrderDecision(value: string): value is BlockedOrderDecision {
  return (BLOCKED_ORDER_DECISIONS as readonly string[]).includes(value)
}

/**
 * What people do to an order, in its history. Every action has a user behind it: nothing is written
 * when an order arrives or its band moves — both are read from SAP's data.
 */
export const BLOCKED_ORDER_ACTION_TYPES = [
  'sent_for_approval',
  'approved',
  'rejected',
  'returned_to_ceb_chain',
  'returned_to_exception_chain',
] as const
export type BlockedOrderActionType = (typeof BLOCKED_ORDER_ACTION_TYPES)[number]

/**
 * The two actions that move an exception payer's orders between the exception chain and the CEB
 * bands: a send into the normal chain (CT-33) and the way back (UAT, 2026-10-29). Routing reads
 * the latest of them: a send puts the order on its band, a return puts it back on the exception
 * chain, and either restarts the chain at its first slot.
 */
export const BLOCKED_ORDER_CHAIN_SWITCH_ACTIONS = [
  'returned_to_ceb_chain',
  'returned_to_exception_chain',
] as const satisfies readonly BlockedOrderActionType[]
export type BlockedOrderChainSwitchAction = (typeof BLOCKED_ORDER_CHAIN_SWITCH_ACTIONS)[number]

export function isBlockedOrderChainSwitch(
  action: BlockedOrderActionType,
): action is BlockedOrderChainSwitchAction {
  return (BLOCKED_ORDER_CHAIN_SWITCH_ACTIONS as readonly string[]).includes(action)
}

/** Translatable business-rule failures (frontend renders `clr.errors.<key>`). */
export const BLOCKED_ORDER_ERRORS = [
  'blockedOrderInvalidState',
  'blockedOrderNotEligible',
  'blockedOrderCommentRequired',
  'blockedOrderUnresolvableBand',
  'blockedOrderNotExceptionRouted',
  /** The order is not an exception payer's order in the normal chain, so there is nothing to bring back. */
  'blockedOrderNotInDoaChain',
  /** The payer's CEB moved the record to another band since the page was read. */
  'blockedOrderBandChanged',
  'blockedOrderAlreadyApproved',
  /** The user is on the live source and this environment has no SAP DCD tables. */
  'blockedOrderSourceUnsupported',
] as const
export type BlockedOrderErrorType = (typeof BLOCKED_ORDER_ERRORS)[number]

export function isBlockedOrderErrorType(value: unknown): value is BlockedOrderErrorType {
  return typeof value === 'string' && (BLOCKED_ORDER_ERRORS as readonly string[]).includes(value)
}

/**
 * Where a user's blocked orders come from: SAP's DCD case tables (`live`), or the cockpit's test
 * orders (`mock`). Per user, so two people can work on different sources at once.
 */
/**
 * The decision's comment goes to SAP as `DCD_NOTES`, a CHAR 255 field on the release/reject IDOC
 * (the XSD on CT-34), so the comment is capped at what SAP keeps rather than cut on the way out.
 */
export const BLOCKED_ORDER_DECISION_COMMENT_MAX_LENGTH = 255

/**
 * A decision SAP has not shown acted on after this long is something to look at: SAP's pipeline
 * rewrites the cases every ~5 minutes, so a healthy hand-off confirms well inside it. The cockpit's
 * banner and the worker's warning share it.
 */
export const BLOCKED_ORDER_SAP_HANDOFF_STALE_MINUTES = 15

/**
 * What SAP's case showed once Fuze's decision reached it (CT-34). SAP never calls back: the worker's
 * reconcile reads the case out of `fuze_cache` and records one of these. `released` and `rejected`
 * are SAP's `doc_status` 2 and 3; `removed` is the case gone from the cache, which BAT may do
 * instead of flipping the status, and is only ever recorded behind the
 * `blockedOrderSapRemovalConfirms` flag.
 */
export const BLOCKED_ORDER_SAP_CONFIRMATIONS = ['released', 'rejected', 'removed'] as const
export type BlockedOrderSapConfirmation = (typeof BLOCKED_ORDER_SAP_CONFIRMATIONS)[number]

/**
 * Where a decided order's hand-off to SAP stands, as the cockpit shows it beside the outcome:
 *   - `not_sent`: decided, not yet accepted by BAT's gateway (never tried, or retrying)
 *   - `sent`: the gateway queued the IDOC; SAP has not shown it acted yet
 *   - `failed`: the gateway refused it (4xx); nothing retries, someone has to look
 *   - `confirmed`: SAP's case agrees with the decision, or is gone
 *   - `conflict`: SAP's case shows the opposite outcome
 * Derived at read time from the snapshot's stamps — see `resolveBlockedOrderSapHandoff`.
 */
export const BLOCKED_ORDER_SAP_HANDOFF_STATUSES = [
  'not_sent',
  'sent',
  'failed',
  'confirmed',
  'conflict',
] as const
export type BlockedOrderSapHandoffStatus = (typeof BLOCKED_ORDER_SAP_HANDOFF_STATUSES)[number]

export type BlockedOrderSapHandoff = {
  status: BlockedOrderSapHandoffStatus
  dispatchedAt: Date | null
  attemptedAt: Date | null
  requestId: string | null
  error: string | null
  confirmedAt: Date | null
  confirmation: BlockedOrderSapConfirmation | null
}

/** `removed` is not a conflict: the case being gone says nothing about which way SAP went. */
export function resolveBlockedOrderSapHandoff(snapshot: {
  outcome: BlockedOrderOutcome
  sapDispatchAttemptedAt: Date | null
  sapDispatchedAt: Date | null
  sapRequestId: string | null
  sapDispatchError: string | null
  sapConfirmedAt: Date | null
  sapConfirmation: BlockedOrderSapConfirmation | null
}): BlockedOrderSapHandoff {
  const confirmation = snapshot.sapConfirmedAt ? snapshot.sapConfirmation : null
  const status: BlockedOrderSapHandoffStatus = confirmation
    ? confirmation === 'removed' || confirmation === snapshot.outcome
      ? 'confirmed'
      : 'conflict'
    : snapshot.sapDispatchError
      ? 'failed'
      : snapshot.sapDispatchedAt
        ? 'sent'
        : 'not_sent'
  return {
    status,
    dispatchedAt: snapshot.sapDispatchedAt,
    attemptedAt: snapshot.sapDispatchAttemptedAt,
    requestId: snapshot.sapRequestId,
    error: snapshot.sapDispatchError,
    confirmedAt: snapshot.sapConfirmedAt,
    confirmation,
  }
}

export const BLOCKED_ORDER_SOURCES = ['live', 'mock'] as const
export type BlockedOrderSource = (typeof BLOCKED_ORDER_SOURCES)[number]
export const DEFAULT_BLOCKED_ORDER_SOURCE: BlockedOrderSource = 'live'
